/**
 * The design tokens, for the styles that stay inline.
 *
 * `theme.css` owns everything a component cannot express — hover, focus, the surfaces, the
 * button variants. This owns the rest: the values a component still writes into a `style`
 * object because they are *layout* rather than appearance, and which must nonetheless be the
 * same values the stylesheet uses.
 *
 * Every entry is a `var(--es-…)` reference rather than a literal, so there is exactly one
 * definition of each colour and it lives in the stylesheet. That is what makes the two halves
 * incapable of drifting apart — the failure this replaces, where the same panel white was
 * written at four different opacities in four different files.
 */

export const colour = Object.freeze({
  /** Body text, and anything that has to be read first. */
  ink: 'var(--es-ink-900)',
  strong: 'var(--es-ink-800)',
  body: 'var(--es-ink-700)',
  muted: 'var(--es-ink-500)',
  faint: 'var(--es-ink-400)',
  line: 'var(--es-ink-200)',
  hairline: 'var(--es-hairline)',
  wash: 'var(--es-ink-100)',
  sunken: 'var(--es-ink-50)',

  accent: 'var(--es-accent)',
  accentInk: 'var(--es-accent-ink)',
  accentSoft: 'var(--es-accent-soft)',

  danger: 'var(--es-danger)',

  surface: 'var(--es-surface)',
  solid: 'var(--es-surface-solid)',
});

/** A four-pixel rhythm. Every gap and pad in the interface is one of these. */
export const space = Object.freeze({
  hair: 2,
  tight: 4,
  snug: 6,
  base: 8,
  wide: 12,
  loose: 16,
});

export const radius = Object.freeze({
  xs: 'var(--es-radius-xs)',
  sm: 'var(--es-radius-sm)',
  md: 'var(--es-radius-md)',
  lg: 'var(--es-radius-lg)',
  pill: '999px',
});

/**
 * Four sizes, and no more.
 *
 * The interface had six, arrived at one component at a time — 10, 11, 12, 13 and two
 * line-heights of 12. A reader cannot tell 12 from 13, so the difference bought nothing and
 * cost the sense that anything was aligned to anything.
 */
export const text = Object.freeze({
  /** Group headings and unit labels. Pair with the `es-eyebrow` class for the caps treatment. */
  micro: '600 10px/1.3 var(--es-font)',
  /** Secondary: hints, shortcuts, counts. */
  small: '11px/1.45 var(--es-font)',
  /** The default for controls and rows. */
  body: '12px/1.4 var(--es-font)',
  /** Section titles and anything that heads a group of controls. */
  title: '600 12px/1.4 var(--es-font)',
  mono: '11px/1.4 var(--es-mono)',
});

export const shadow = Object.freeze({
  /** A panel resting on the drawing. */
  panel: 'var(--es-shadow-1)',
  /** A menu floating above a panel. */
  float: 'var(--es-shadow-2)',
});
